import { FC, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import {
  ActionIcon,
  ColorSwatch,
  Group,
  Menu,
  Tooltip,
  Divider,
  Button,
  Box,
} from '@mantine/core';
import {
  IconBold,
  IconItalic,
  IconUnderline,
  IconStrikethrough,
  IconH1,
  IconH2,
  IconH3,
  IconList,
  IconListNumbers,
  IconAlignLeft,
  IconAlignCenter,
  IconAlignRight,
  IconAlignJustified,
  IconLink,
  IconLinkOff,
  IconHighlight,
  IconSubscript,
  IconSuperscript,
  IconTable,
  IconRowInsertBottom,
  IconRowInsertTop,
  IconColumnInsertLeft,
  IconColumnInsertRight,
  IconTableOff,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconClearFormatting,
  IconFileTypePdf,
  IconEye,
  IconPrinter,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import styles from './ReportEditor.module.css';

export interface ReportEditorToolbarProps {
  editor: Editor;
  /** Called when PDF preview is requested */
  onPreviewPDF?: () => void;
  /** Called when PDF export is requested */
  onExportPDF?: () => void;
  /** Called when print is requested */
  onPrint?: () => void;
  /** Whether PDF operations are loading */
  pdfLoading?: boolean;
}

const HIGHLIGHT_COLOR_KEYS = [
  { color: '#FFEB3B', labelKey: 'reportEditor.toolbar.highlightYellow' },
  { color: '#90EE90', labelKey: 'reportEditor.toolbar.highlightGreen' },
  { color: '#FFB6C1', labelKey: 'reportEditor.toolbar.highlightPink' },
  { color: '#87CEEB', labelKey: 'reportEditor.toolbar.highlightBlue' },
  { color: '#DDA0DD', labelKey: 'reportEditor.toolbar.highlightPurple' },
  { color: '#FFA07A', labelKey: 'reportEditor.toolbar.highlightOrange' },
] as const;

/**
 * Toolbar for the Report Editor with formatting controls
 * @param root0 - Component props
 * @param root0.editor - Tiptap editor instance
 * @param root0.onPreviewPDF - Called when PDF preview is requested
 * @param root0.onExportPDF - Called when PDF export is requested
 * @param root0.onPrint - Called when print is requested
 * @param root0.pdfLoading - Whether PDF operations are loading
 * @returns Toolbar component with formatting buttons
 */
export const ReportEditorToolbar: FC<ReportEditorToolbarProps> = ({
  editor,
  onPreviewPDF,
  onExportPDF,
  onPrint,
  pdfLoading = false,
}) => {
  const { t } = useTranslation('lims');
  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  return (
    <Box p="xs" className={styles.toolbar}>
      <Group gap="xs" wrap="wrap">
        {/* Undo/Redo */}
        <Group gap={4}>
          <Tooltip label={t('reportEditor.toolbar.undo')}>
            <ActionIcon
              variant={editor.can().undo() ? 'default' : 'subtle'}
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
            >
              <IconArrowBackUp size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('reportEditor.toolbar.redo')}>
            <ActionIcon
              variant={editor.can().redo() ? 'default' : 'subtle'}
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
            >
              <IconArrowForwardUp size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Divider orientation="vertical" />

        {/* Text formatting */}
        <Group gap={4}>
          <Tooltip label={t('reportEditor.toolbar.bold')}>
            <ActionIcon
              variant={editor.isActive('bold') ? 'filled' : 'default'}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <IconBold size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('reportEditor.toolbar.italic')}>
            <ActionIcon
              variant={editor.isActive('italic') ? 'filled' : 'default'}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <IconItalic size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('reportEditor.toolbar.underline')}>
            <ActionIcon
              variant={editor.isActive('underline') ? 'filled' : 'default'}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
              <IconUnderline size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('reportEditor.toolbar.strikethrough')}>
            <ActionIcon
              variant={editor.isActive('strike') ? 'filled' : 'default'}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            >
              <IconStrikethrough size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Divider orientation="vertical" />

        {/* Headings */}
        <Group gap={4}>
          <Tooltip label={t('reportEditor.toolbar.heading1')}>
            <ActionIcon
              variant={
                editor.isActive('heading', { level: 1 }) ? 'filled' : 'default'
              }
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 1 }).run()
              }
            >
              <IconH1 size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('reportEditor.toolbar.heading2')}>
            <ActionIcon
              variant={
                editor.isActive('heading', { level: 2 }) ? 'filled' : 'default'
              }
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 2 }).run()
              }
            >
              <IconH2 size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('reportEditor.toolbar.heading3')}>
            <ActionIcon
              variant={
                editor.isActive('heading', { level: 3 }) ? 'filled' : 'default'
              }
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 3 }).run()
              }
            >
              <IconH3 size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Divider orientation="vertical" />

        {/* Lists */}
        <Group gap={4}>
          <Tooltip label={t('reportEditor.toolbar.bulletList')}>
            <ActionIcon
              variant={editor.isActive('bulletList') ? 'filled' : 'default'}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <IconList size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('reportEditor.toolbar.orderedList')}>
            <ActionIcon
              variant={editor.isActive('orderedList') ? 'filled' : 'default'}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <IconListNumbers size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Divider orientation="vertical" />

        {/* Text alignment */}
        <Group gap={4}>
          <Tooltip label={t('reportEditor.toolbar.alignLeft')}>
            <ActionIcon
              variant={
                editor.isActive({ textAlign: 'left' }) ? 'filled' : 'default'
              }
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
            >
              <IconAlignLeft size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('reportEditor.toolbar.alignCenter')}>
            <ActionIcon
              variant={
                editor.isActive({ textAlign: 'center' }) ? 'filled' : 'default'
              }
              onClick={() =>
                editor.chain().focus().setTextAlign('center').run()
              }
            >
              <IconAlignCenter size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('reportEditor.toolbar.alignRight')}>
            <ActionIcon
              variant={
                editor.isActive({ textAlign: 'right' }) ? 'filled' : 'default'
              }
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
            >
              <IconAlignRight size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('reportEditor.toolbar.alignJustify')}>
            <ActionIcon
              variant={
                editor.isActive({ textAlign: 'justify' }) ? 'filled' : 'default'
              }
              onClick={() =>
                editor.chain().focus().setTextAlign('justify').run()
              }
            >
              <IconAlignJustified size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Divider orientation="vertical" />

        {/* Super/subscript */}
        <Group gap={4}>
          <Tooltip label={t('reportEditor.toolbar.superscript')}>
            <ActionIcon
              variant={editor.isActive('superscript') ? 'filled' : 'default'}
              onClick={() => editor.chain().focus().toggleSuperscript().run()}
            >
              <IconSuperscript size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('reportEditor.toolbar.subscript')}>
            <ActionIcon
              variant={editor.isActive('subscript') ? 'filled' : 'default'}
              onClick={() => editor.chain().focus().toggleSubscript().run()}
            >
              <IconSubscript size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Divider orientation="vertical" />

        {/* Highlight */}
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <Tooltip label={t('reportEditor.toolbar.highlight')}>
              <ActionIcon
                variant={editor.isActive('highlight') ? 'filled' : 'default'}
              >
                <IconHighlight size={18} />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{t('reportEditor.toolbar.highlightColor')}</Menu.Label>
            <Group gap="xs" p="xs">
              {HIGHLIGHT_COLOR_KEYS.map(({ color, labelKey }) => (
                <Tooltip key={color} label={t(labelKey)}>
                  <ColorSwatch
                    color={color}
                    size={24}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      editor.chain().focus().toggleHighlight({ color }).run()
                    }
                  />
                </Tooltip>
              ))}
            </Group>
            <Menu.Divider />
            <Menu.Item
              onClick={() => editor.chain().focus().unsetHighlight().run()}
            >
              {t('reportEditor.toolbar.removeHighlight')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>

        {/* Link */}
        <Group gap={4}>
          <Tooltip label={t('reportEditor.toolbar.addLink')}>
            <ActionIcon
              variant={editor.isActive('link') ? 'filled' : 'default'}
              onClick={setLink}
            >
              <IconLink size={18} />
            </ActionIcon>
          </Tooltip>
          {editor.isActive('link') && (
            <Tooltip label={t('reportEditor.toolbar.removeLink')}>
              <ActionIcon
                variant="default"
                onClick={() => editor.chain().focus().unsetLink().run()}
              >
                <IconLinkOff size={18} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        <Divider orientation="vertical" />

        {/* Table controls */}
        <Menu shadow="md" width={220}>
          <Menu.Target>
            <Tooltip label={t('reportEditor.toolbar.table')}>
              <ActionIcon variant="default">
                <IconTable size={18} />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{t('reportEditor.toolbar.table')}</Menu.Label>
            <Menu.Item
              leftSection={<IconTable size={16} />}
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                  .run()
              }
            >
              {t('reportEditor.toolbar.insertTable')}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              leftSection={<IconRowInsertBottom size={16} />}
              onClick={() => editor.chain().focus().addRowAfter().run()}
              disabled={!editor.can().addRowAfter()}
            >
              {t('reportEditor.toolbar.addRowBelow')}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconRowInsertTop size={16} />}
              onClick={() => editor.chain().focus().addRowBefore().run()}
              disabled={!editor.can().addRowBefore()}
            >
              {t('reportEditor.toolbar.addRowAbove')}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconColumnInsertRight size={16} />}
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              disabled={!editor.can().addColumnAfter()}
            >
              {t('reportEditor.toolbar.addColumnRight')}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconColumnInsertLeft size={16} />}
              onClick={() => editor.chain().focus().addColumnBefore().run()}
              disabled={!editor.can().addColumnBefore()}
            >
              {t('reportEditor.toolbar.addColumnLeft')}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              leftSection={<IconTableOff size={16} />}
              color="red"
              onClick={() => editor.chain().focus().deleteTable().run()}
              disabled={!editor.can().deleteTable()}
            >
              {t('reportEditor.toolbar.deleteTable')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>

        <Divider orientation="vertical" />

        {/* Clear formatting */}
        <Tooltip label={t('reportEditor.toolbar.clearFormatting')}>
          <ActionIcon
            variant="default"
            onClick={() =>
              editor.chain().focus().clearNodes().unsetAllMarks().run()
            }
          >
            <IconClearFormatting size={18} />
          </ActionIcon>
        </Tooltip>

        {/* Export/Print actions - show spacer when any PDF/print handler is provided */}
        {Boolean(onPreviewPDF || onExportPDF || onPrint) && (
          <>
            <Box flex={1} />
            <Divider orientation="vertical" />
          </>
        )}

        <Group gap={4}>
          {onPreviewPDF && (
            <Tooltip label={t('reportEditor.pdf.preview')}>
              <Button
                variant="subtle"
                size="compact-sm"
                leftSection={<IconEye size={16} />}
                onClick={onPreviewPDF}
                loading={pdfLoading}
              >
                {t('reportEditor.pdf.preview')}
              </Button>
            </Tooltip>
          )}

          {onExportPDF && (
            <Tooltip label={t('reportEditor.pdf.export')}>
              <Button
                variant="light"
                size="compact-sm"
                leftSection={<IconFileTypePdf size={16} />}
                onClick={onExportPDF}
                loading={pdfLoading}
              >
                {t('reportEditor.pdf.export')}
              </Button>
            </Tooltip>
          )}

          {onPrint && (
            <Tooltip label={t('reportEditor.pdf.print')}>
              <ActionIcon
                variant="default"
                onClick={onPrint}
                disabled={pdfLoading}
              >
                <IconPrinter size={18} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>
    </Box>
  );
};

export default ReportEditorToolbar;
